import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { Subject } from 'rxjs';
import { UserAccessService } from '../../../shared/services/user-access.service';
import { ToastService } from '../../../shared/services/toast.service';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements OnInit {
    settings: Settings[];
    selectedItem: string;
    isSuperUser: boolean;
    isClientPowerUser: boolean;
    public eventsSubject: Subject<void> = new Subject<void>();
    linkTitle: string;
    moduleLink: string;
    initialLoad = true;

    defaultItem: SettingModule = {
        name: 'My Record',
        underConstruction: false,
        key: 'user-profile',
    };

    constructor(
        private _route: ActivatedRoute,
        private router: Router,
        private userAccessService: UserAccessService,
        private impersonationService: ImpersonationService,
        private toastService: ToastService,
        private currentUserStateService: CurrentUserStateService
    ) {
        // This block allows page to reload to new sub-component when navigated through routerLink
        this.router.events.subscribe((event) => {
            if (this.initialLoad == false) {
                if (event instanceof NavigationEnd) {
                    let originalPage: string = this._route.snapshot.paramMap.get('key');

                    if (originalPage) {
                        this.selectedItem = originalPage;
                    }
                }
            }
        });
    }
    ngOnInit() {
        this.isClientPowerUser = this.impersonationService.isPowerUser();
        if (
            this.currentUserStateService.isLoggedIn() &&
            this.currentUserStateService.isLBASuperUser() &&
            !this.impersonationService.isImpersonatingUser()
        ) {
            this.isSuperUser = true;
        }

        // Data used by select menu.
        this.settings = [
            {
                name: 'Profile',
                expanded: true,
                modules: [
                    {
                        name: 'My Record',
                        underConstruction: false,
                        key: 'user-profile',
                    },
                    //{
                    //    name: 'Account',
                    //    key: 'account',
                    //    subModules: [
                    //        { name: 'Account Details', key: 'account-details' },
                    //        { name: 'Change Password', key: 'change-password' },
                    //    ],
                    //},
                    { name: 'Notifications', key: 'notifications' },
                    { name: 'Scheduled Reports', key: 'scheduled-reports' },
                    { name: 'Subscriptions', key: 'user-subscriptions' },
                ],
            },
            {
                name: 'Company',
                expanded: true,
                powerUserRequired: true,
                modules: [
                    {
                        name: 'Integration',
                        key: 'integration-setup',
                        subModules: [
                            //{ name: 'Delete Loans', key: 'delete-loan' },
                            {
                                name: 'Product Mapping',
                                key: 'client-product-mapping',
                            },
                            {
                                name: 'Loan Characteristics Mapping',
                                key: 'loan-characteristics-mapping',
                            },
                            {
                                name: 'General Ledger Mapping',
                                key: 'general-ledger-mapping',
                            },
                        ],
                    },
                    {
                        name: 'Employees & Users',
                        key: 'roles-users',
                        subModules: [
                            {
                                name: 'Employee Hours',
                                key: 'employee-hours',
                            },
                            {
                                name: 'Employees',
                                underConstruction: false,
                                key: 'employees',
                            },
                            {
                                name: 'Position Management',
                                key: 'role-management',
                            },
                            { name: 'User Management', key: 'user-management' },
                            { name: 'Login Activity', key: 'login-activity' },
                        ],
                    },
                    {
                        name: 'Performance',
                        key: 'performance',
                        subModules: [
                            { name: 'KPIs', key: 'performance-kpi-config' },
                            { name: 'KPI Profiles', key: 'kpi-profiles' },
                            {
                                name: 'Branch Projections',
                                key: 'branch-projections-config',
                            },
                        ],
                    },
                    {
                        name: 'Loans',
                        key: 'loans',
                        subModules: [
                            { name: 'Invoiced Loans', key: 'invoiced-loans' },
                            { name: 'Delete Loans', key: 'delete-loan' },
                        ],
                    },
                    {
                        name: 'Notifications',
                        key: 'notification-options',
                        subModules: [
                            { name: 'Types', key: 'notification-types' },
                            { name: 'Subscriptions', key: 'subscriptions' },
                        ],
                    },
                    {
                        name: 'eClose ROI Settings',
                        key: 'eclose-roi-settings',
                    },
                ],
            },
            {
                name: 'nCino Admin',
                lbaOnly: true,
                modules: [
                    { name: 'Clients', key: 'lba-clients' },
                    { name: 'Organizational Setup', key: 'hierarchy-levels' },
                    { name: 'File Upload', key: 'file-upload' },
                    { name: 'Manage Enrollments', key: 'lba-subscriptions' },
                    { name: 'System Users', key: 'super-users' },
                    { name: 'Login Activity', key: 'lba-login-activity' },
                    {
                        name: 'Domains',
                        key: 'domains',
                        subModules: [
                            {
                                name: 'Domain Management',
                                key: 'domain-management',
                            },
                        ],
                    },
                    {
                        name: 'Milestones & Events',
                        key: 'milestones-events',
                        subModules: [
                            {
                                name: 'Milestones Management',
                                key: 'milestones-events',
                            },
                        ],
                    },
                    { name: 'Client Health', key: 'company-health' },
                ],
            },
        ];

        // Remove Super Admin settings for modules and sub modules.
        if (!this.isSuperUser) {
            this.settings = this.settings.filter((s) => !s.lbaOnly);
            this.settings.forEach((setting) => {
                setting.modules = setting.modules.filter((module) => !module.underConstruction);
                setting.modules.forEach((module) => {
                    if (!module.subModules) {
                        return;
                    }
                    module.subModules = module.subModules.filter(
                        (subModule) =>
                            !subModule.underConstruction &&
                            this.userAccessService.hasAccessToPage(`/settings/${subModule.key}`)
                    );
                });

                // Remove module if object used to have subModules but no longer does
                setting.modules = setting.modules.filter((sm) => {
                    if (Object.keys(sm).includes('subModules')) {
                        if (sm.subModules.length == 0) {
                            return false;
                        }
                    }

                    return true;
                });
            });
        }

        // Remove Company settings for modules and sub modules for non-power users.
        if (!(this.isClientPowerUser || this.isSuperUser)) {
            this.settings = this.settings.filter((s) => !s.powerUserRequired);
        }

        this.settings = this.settings.filter((x) => x.modules && x.modules.length !== 0);

        let originalPage: string = this._route.snapshot.paramMap.get('key');
        if (originalPage) {
            this.selectedItem = originalPage;
        } else {
            this.selectItem(this.defaultItem);
        }

        this.initialLoad = false;
    }

    setDefaultSelectedItem(): void {
        this._route.params.subscribe((p) => {
            if (p && p['key']) {
                if (
                    this.settings.find((setting) => {
                        return setting.modules.some((module) => {
                            if (module.key === p['key']) {
                                return true;
                            } else if (module.subModules) {
                                return (
                                    module.subModules.findIndex(
                                        (subModule) => subModule.key === p['key']
                                    ) !== -1
                                );
                            }
                        });
                    })
                ) {
                    this.selectedItem = p['key'];
                }
            }

            if (!this.selectedItem) this.selectedItem = 'user-profile';
        });
    }

    selectItem(item: SettingModule, subItem?: SettingSubModule): void {
        if (item.subModules && item.subModules.length > 0) {
            if (!subItem) {
                this.sectionExpand(item);
            }
        }

        let hasAccess = false;

        if (subItem) {
            if (subItem.key === 'employees' || subItem.key === 'branch') {
                this.emitEventToChild();
            }

            hasAccess = this.userAccessService.hasAccessToPage(`/settings/${subItem.key}`);

            if (hasAccess || this.isSuperUser) {
                this.selectedItem = subItem.key;
            } else {
                this.toastService.warning("You don't have access to this page.");
            }
        } else {
            if (!item.subModules) {
                hasAccess = this.userAccessService.hasAccessToPage(`/settings/${item.key}`);

                if (hasAccess || this.isSuperUser) {
                    this.selectedItem = item.key;
                } else {
                    this.toastService.warning("You don't have access to this page.");
                }
            }
        }

        if (hasAccess || this.isSuperUser) {
            this.router.navigate(['settings', this.selectedItem]);
        }

        if (this.selectedItem === 'user-profile') {
            this.linkTitle = '';
            this.moduleLink = '';
        } else {
            this.linkTitle = this.moduleLink = 'settings';
        }
    }

    sectionExpand(item: Settings): void {
        item.expanded = !item.expanded;
    }

    subSectionExpand(item: SettingSubModule): void {
        item.expanded = !item.expanded;
    }

    isSelected(key: string): boolean {
        return this.selectedItem === key;
    }

    moduleHasNotBeenCreated(itemKey) {
        let items = [
            'users',
            'account',
            'forecasting-planning',
            'company-billing',
            'company-integrations',
            'company-templates',
            'hierarchy-cost-center-mapping',
            'hierarchy-branches',
            'performance-scorecards',
            'forecasting-ledger',
            'forecasting-planning',
        ];
        return items.includes(itemKey);
    }

    emitEventToChild() {
        this.eventsSubject.next();
    }
}

export interface Settings {
    name: string;
    modules?: SettingModule[];
    lbaOnly?: boolean;
    expanded?: boolean;
    powerUserRequired?: boolean;
}

export interface SettingModule {
    name: string;
    key: string;
    subModules?: SettingSubModule[];
    expanded?: boolean;
    underConstruction?: boolean;
}

export interface SettingSubModule {
    name: string;
    key: string;
    expanded?: boolean;
    underConstruction?: boolean;
}
